import { useQuery } from "@tanstack/react-query"
import api from "api"
import { queries } from "api/queries"
import {
  AccessStatus,
  DeliveryMode,
  LearningItemDetails,
  PlannerAccess,
} from "api/resources/learningItems/types"
import Monitor from "assets/Fallback/monitor.svg"
import ErrorPage from "components/ErrorPage"
import Fallback from "components/Fallback"
import FullPageLoading from "components/FullPageLoading"
import { PageWithMeta } from "components/Metadata"
import PageContainer from "components/PageContainer"
import { getPgCTLProgramName } from "features/PgCTLEnrollmentAgreement/utils"
import { PropsWithChildren } from "react"
import { Outlet, useOutletContext, useParams } from "react-router-dom"
import {
  IS_DEV_ENVIRONMENT,
  IS_LOCALHOST,
  learningItemSlugs,
} from "utils/constants"
import { useEnrollments } from "utils/hooks/useEnrollments"

import LearningItemHeader from "../Header"

type ContextType = {
  qualification: LearningItemDetails
  accessStatus: AccessStatus
  planner: PlannerAccess
}

const MetaWrapper = ({ children }: PropsWithChildren) => {
  const { learningItemType } = useParams() as {
    learningItemType: string
  }

  if (
    learningItemType === "qualification" ||
    learningItemType === "certification"
  )
    return <PageWithMeta name={learningItemType}>{children}</PageWithMeta>

  return <ErrorPage errorStatus={404} />
}

const voidTab = { name: () => "", link: "" }

const Qualification = () => {
  const { slug, learningItemType } = useParams() as {
    slug: string
    learningItemType: string
  }

  const {
    pgctl: { deliveryMode, isNewDeliveryMode },
  } = useEnrollments()

  const { data, isLoading, isError } = useQuery({
    queryFn: () =>
      api.learningItems.getLearningItemDetails({
        urlParams: {
          itemType: learningItemType,
          itemSlug: slug,
        },
      }),
    queryKey: queries.learningItems.details(learningItemType, slug).queryKey,
  })
  const planner = useQuery({
    enabled: Boolean(data),
    queryFn: () =>
      api.learningItems.getPlanner({
        urlParams: {
          itemType: learningItemType,

          itemId: data!.id,
        },
      }),
  })

  if (isLoading || planner.isLoading)
    return (
      <MetaWrapper>
        <FullPageLoading />
      </MetaWrapper>
    )

  if (isError || planner.isError)
    return (
      <MetaWrapper>
        <Fallback
          data={{
            title: "Something went wrong",
            description: "Please contact care@suraasa.com",
            image: Monitor,
          }}
          className="pt-4"
        />
      </MetaWrapper>
    )

  // Guard against the brief render cycle where planner becomes enabled but
  // hasn't started fetching yet (planner.isLoading is false but planner.data
  // is still undefined), which would crash on planner.data.accessStatus below.
  if (!data || !planner.data) {
    return (
      <MetaWrapper>
        <FullPageLoading />
      </MetaWrapper>
    )
  }

  const getClassesTab = () => {
    if (learningItemType !== "qualification") return voidTab

    if (slug === learningItemSlugs.pgctl) {
      const modes = [
        DeliveryMode.PROFESSIONAL,
        DeliveryMode.EXECUTIVE,
        DeliveryMode.TDC,
      ]
      if (deliveryMode && modes.includes(deliveryMode))
        return { name: () => "Extra Classes", link: "classes" }
      if (isNewDeliveryMode) return voidTab
    }

    return { name: () => "Classes", link: "classes" }
  }

  const getAssessmentsTab = () => {
    if (learningItemType !== "qualification") return voidTab

    if (slug === learningItemSlugs.pgctl) {
      if (isNewDeliveryMode) return voidTab
    }

    return { name: () => "Assessments", link: "assessments" }
  }

  const getCertificatesTab = () => {
    if (learningItemType !== "qualification") return voidTab

    return { name: () => "Certificates", link: "certificates" }
  }

  const getGradebookTab = () => {
    // We want to show gradebook only for med and pgctl
    if (slug === learningItemSlugs.med || slug === learningItemSlugs.pgctl) {
      return { name: () => "Gradebook", link: "gradebook" }
    }

    return voidTab
  }

  const getAnalyticsTab = () => {
    if (isNewDeliveryMode && slug === learningItemSlugs.pgctl) {
      return { name: () => "Analytics", link: "analytics" }
    }
    return voidTab
  }

  const tabs = [
    { name: () => "Overview", link: "overview" },
    getAssessmentsTab(),
    getClassesTab(),
    { name: () => "Resources", link: "resources" },
    getAnalyticsTab(),
    getGradebookTab(),
    getCertificatesTab(),
    { name: () => "About", link: "about" },
    // { name: "Leaderboard", link: "leaderboard" },
  ].filter(x => x.link)

  const name =
    deliveryMode && (IS_LOCALHOST || IS_DEV_ENVIRONMENT)
      ? `${data.name} - ${getPgCTLProgramName(deliveryMode).modeDisplay}`
      : data.name

  return (
    <MetaWrapper>
      {/* Container is handled inside */}
      {data.type !== "course" && (
        <LearningItemHeader
          image={data.bannerImage}
          itemSlug={data.slug}
          itemName={name}
          data={tabs}
          className="mb-3"
        />
      )}
      {/* Bottom padding for each page so content doesn't get cropped */}
      <PageContainer className="pb-10">
        <Outlet
          context={{
            qualification: data,
            planner: planner.data,
            accessStatus: planner.data.accessStatus,
          }}
        />
      </PageContainer>
    </MetaWrapper>
  )
}

export default Qualification

export function useQualificationDetails() {
  return useOutletContext<ContextType>()
}
